import { useState, useMemo } from 'react';
import {
  useWorkAssignments,
  useWorkgroupOptionsForIncident,
} from '@/components/IAP/hooks/workAssignmentHooks';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Field, FieldGroup, FieldLabel, FieldSet } from '@/components/ui/field';

import {
  useCreateWorkAssignmentListAttachment,
  useWorkAssignmentListAttachments,
} from '@/components/IAP/hooks/assignmentListHooks';

type AddWalaDialogProps = {
  needsExistingAssignments: boolean;
  operationalPeriodId?: string;
  isDialogOpen: boolean;
  closeDialog: () => void;
};

const AddWalaDialog = ({
  needsExistingAssignments,
  operationalPeriodId,
  isDialogOpen,
  closeDialog,
}: AddWalaDialogProps) => {
  const [selectedWorkgroupId, setSelectedWorkgroupId] = useState<string | null>(
    null
  );

  const createWala = useCreateWorkAssignmentListAttachment();

  const handleAddAssignmentFrom215 = () => {
    if (!operationalPeriodId) return;

    createWala.mutate({
      workGroupId: selectedWorkgroupId!,
      operationalPeriodId,
    });

    setSelectedWorkgroupId(null);
  };

  const { data: workAssignmentListAttachments } =
    useWorkAssignmentListAttachments();

  // we have to filter this list to only show workgroups that have not already been added to the assignment list for this period
  const unfilteredWorkgroupOptions = useWorkgroupOptionsForIncident();

  const { data: workAssignments } = useWorkAssignments();

  const workgroupOptions = useMemo(() => {
    return unfilteredWorkgroupOptions.filter(
      (option) =>
        needsExistingAssignments ==
        workAssignments?.some(
          (wa) =>
            wa.work_group_id === option.value &&
            wa.operational_period_id === operationalPeriodId
        )
    );
  }, [
    unfilteredWorkgroupOptions,
    needsExistingAssignments,
    workAssignments,
    operationalPeriodId,
  ]);

  const assignedWorkgroups = useMemo(() => {
    return workAssignmentListAttachments
      ? workAssignmentListAttachments
          .filter(
            (attachment) =>
              attachment.operationalPeriodId === operationalPeriodId
          )
          .map((attachment) => attachment.workGroupId)
      : [];
  }, [workAssignmentListAttachments, operationalPeriodId]);

  const assignedWorkgroupOptions = useMemo(() => {
    return workgroupOptions.filter((option) =>
      assignedWorkgroups.includes(option.value)
    );
  }, [workgroupOptions, assignedWorkgroups]);

  const unAssignedWorkgroupOptions = useMemo(() => {
    return workgroupOptions.filter(
      (option) => !assignedWorkgroups.includes(option.value)
    );
  }, [workgroupOptions, assignedWorkgroups]);

  return (
    <>
      <Dialog
        open={isDialogOpen}
        onOpenChange={() => {
          setSelectedWorkgroupId(null);
          closeDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Assignment Card from {needsExistingAssignments ? "ICS-215" : "Scratch"}</DialogTitle>
          </DialogHeader>
          <div className="tw:flex tw:flex-col tw:gap-4">
            <FieldSet>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="workgroup-select">Workgroup</FieldLabel>
                  <Select
                    id="workgroup-select"
                    items={unAssignedWorkgroupOptions}
                    onValueChange={setSelectedWorkgroupId}
                  >
                    <SelectTrigger className="tw:w-[180px]" size="default">
                      <SelectValue placeholder="Select Workgroup" />
                    </SelectTrigger>
                    <SelectContent alignItemWithTrigger>
                      <SelectGroup>
                        {assignedWorkgroupOptions.map((t) => (
                          <SelectItem key={t.value} value={t.value} disabled>
                            {t.label} (Already Added)
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        {/* We need to also show the workgroups that have already been added */}
                        {unAssignedWorkgroupOptions.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </Field>
              </FieldGroup>
            </FieldSet>
          </div>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>
              Close
            </DialogClose>
            <Button
              onClick={() => {
                handleAddAssignmentFrom215();
                closeDialog();
              }}
              disabled={!selectedWorkgroupId}
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddWalaDialog;
